import { BlogTile } from "@faire/deprecated-retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/BlogCarousel/__internal__/BlogTile";
import { useQuery as useBlogCarouselQuery } from "@faire/web-api--source/endpoints/www/api/logged-out-homepage/blog-carousel/get-hooks";
import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api--source/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Arrow } from "@faire/web/slate/icons/Arrow";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { Carousel } from "@faire/web/ui-labs/Carousel/Carousel";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React from "react";

import { useIsLoggedOutHomepageV2 } from "../../useIsLoggedOutHomepageV2";

export const BlogCarousel: React.FC = () => {
  // Trace component render for homepage performance monitoring
  // Related to FD-275664: Add OTEL tracing to logged out homepage
  useTraceComponentRender("BlogCarousel");

  const { isMobile } = useViewport();
  const isHomepageRedesignV2 = useIsLoggedOutHomepageV2();
  const ref = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView(
      "blog_carousel",
      isHomepageRedesignV2 ? "v2" : "v1"
    )
  );
  const { data } = useBlogCarouselQuery({
    gcTime: Infinity,
    staleTime: Infinity,
  });

  if (!data?.carousel_tiles?.length) {
    return null;
  }

  const { title, cta_link_url, cta_link_text, carousel_tiles } = data;

  return (
    <Column
      className={
        isHomepageRedesignV2
          ? "mx-4 my-12 md:mx-8 lg:mx-12 2xl:mx-20"
          : "w-full max-w-[1088px] py-12 pr-0 pl-8 lg:mx-auto lg:py-[90px] lg:pr-0"
      }
      data-test-id="blog-carousel"
      gap="4x"
      ref={ref}
    >
      <Row justify="space-between">
        <Typography
          variant={
            isHomepageRedesignV2
              ? {
                  mobileAndAbove: "pageHeaderSerifRegular",
                  xLargeAndAbove: "displaySSerifRegular",
                  xxLargeDesktop: "displayMSerifRegular",
                }
              : {
                  mobileAndAbove: "displaySSerifRegular",
                  desktopAndAbove: "displayMSerifRegular",
                }
          }
          style={{
            paddingRight: `${getSpacing("4x")}`,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>
        <ResponsiveContentV2
          desktopAndAbove={
            <CTADesktopAndAbove
              ctaLink={cta_link_url}
              ctaLinkText={cta_link_text}
            />
          }
        />
      </Row>
      <Carousel
        items={carousel_tiles ?? []}
        renderItem={(blog) => <BlogTile blogTile={blog} />}
        freeFlow={!isHomepageRedesignV2 || isMobile}
        itemsPerSlide={isHomepageRedesignV2 ? 4 : undefined}
        itemGap={16}
        showNavigationArrows={false}
        loop={false}
        noMobilePadding
        onVisible={() => {
          trackLoggedOutRetailersLoggedOutHomeRedesignView(
            "blog_carousel-onscroll",
            isHomepageRedesignV2 ? "v2" : "v1"
          );
        }}
      />
      <ResponsiveContentV2
        tabletAndBelow={
          <CTATabletAndBelow
            ctaLink={cta_link_url}
            ctaLinkText={cta_link_text}
          />
        }
      />
    </Column>
  );
};

const CTADesktopAndAbove: React.FC<{
  ctaLink: string | undefined;
  ctaLinkText: string | undefined;
}> = ({ ctaLink, ctaLinkText }) => {
  const isHomepageRedesignV2 = useIsLoggedOutHomepageV2();
  return (
    <Button
      variant="tertiary"
      linkTo={ctaLink}
      className={isHomepageRedesignV2 ? "group" : "group pr-4"}
      onClick={() => {
        trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
          "blog-carousel-cta",
          ctaLink ?? "",
          isHomepageRedesignV2 ? "v2" : "v1"
        );
      }}
    >
      {ctaLinkText}
      {isHomepageRedesignV2 ? null : (
        <>
          <Spacer width="1x" />
          <Arrow
            direction="right"
            className="mr-8! transition-all duration-500 ease-in-out group-hover:translate-x-2.5"
          />
        </>
      )}
    </Button>
  );
};

const CTATabletAndBelow: React.FC<{
  ctaLink: string | undefined;
  ctaLinkText: string | undefined;
}> = ({ ctaLink, ctaLinkText }) => {
  const isHomepageRedesignV2 = useIsLoggedOutHomepageV2();
  return (
    <Button
      variant="secondary"
      className="hover:bg-fs-surface-primary-inverse! bg-transparent!"
      style={{ width: "fit-content" }}
      linkTo={ctaLink}
      onClick={() => {
        trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
          "blog-carousel-cta",
          ctaLink ?? "",
          isHomepageRedesignV2 ? "v2" : "v1"
        );
      }}
    >
      {ctaLinkText}
    </Button>
  );
};
